'use client';

import React from 'react';
import { cn } from '@/lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center font-semibold rounded-full transition-[transform,background-color,box-shadow,opacity,border-color,color] duration-150 ease-out focus-visible:ring-2 focus-visible:ring-brand-lime focus-visible:ring-offset-2 focus-visible:ring-offset-brand-black select-none disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary:
      'bg-brand-lime text-brand-black hover:bg-brand-lime-light active:bg-brand-lime-dark shadow-lime-sm hover:shadow-lime',
    secondary:
      'bg-brand-surface-2 text-brand-white border border-brand-gray-muted hover:bg-brand-surface-3 hover:border-brand-lime/40',
    ghost:
      'bg-transparent text-brand-gray-light hover:text-brand-white hover:bg-brand-surface-2',
    danger:
      'bg-brand-red text-white hover:bg-brand-red-dark active:scale-95',
    outline:
      'bg-transparent text-brand-lime border border-brand-lime hover:bg-brand-lime hover:text-brand-black',
  };

  const sizes = {
    sm: 'h-8 px-4 text-xs gap-1.5',
    md: 'h-10 px-6 text-sm gap-2',
    lg: 'h-12 px-8 text-base gap-2.5',
  };

  return (
    <button
      disabled={disabled || loading}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        !disabled && !loading && 'active:scale-[0.97]',
        className
      )}
      {...props}
    >
      {loading && (
        <svg
          className="w-4 h-4 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12" cy="12" r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
